'use client';

import * as React from 'react';
import Link from 'next/link';
import { ArrowRightIcon, Clock, PlayIcon, SparklesIcon, Users, Zap } from 'lucide-react';

import { routes } from '@workspace/routes';
import { cn } from '@workspace/ui/lib/utils';
import { ComingSoonModal } from '../coming-soon-modal';

export function Hero(): React.JSX.Element {
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(false);
  const [currentStat, setCurrentStat] = React.useState(0);
  const [showComingSoon, setShowComingSoon] = React.useState(false);

  // Rotating stats for dynamic engagement
  const stats = [
    { icon: Users, value: "Beta", label: "early access available" },
    { icon: Clock, value: "2.5min", label: "average creation time" },
    { icon: SparklesIcon, value: "AI", label: "powered creativity" },
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStat((prev) => (prev + 1) % stats.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-[85vh] flex items-center justify-center py-24 lg:py-32 overflow-hidden bg-[#0E0F1A]">
      {/* Cinematic Theater Background */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(at 30% 50%, #1A1B29 0%, #0E0F1A 70%)' }} />

      {/* Holographic Glow Orb */}
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />

      <div className="container px-6 relative z-10">
        <div className="mx-auto max-w-6xl">
          <div className="flex justify-center">

            {/* Left Content - Improved Hierarchy */}
            <div className="text-center">

              {/* Trust Badge - Build Credibility First */}
              <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md rounded-full px-4 py-2 mb-8 shadow-sm border border-white/20 mx-auto">
                <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse-slow" />
                <span className="text-sm font-medium text-white">Early Access Available</span>
              </div>

              {/* Hero Headline - Cleaner Typography */}
              <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight tracking-tight mx-auto max-w-3xl">
                <span className="whitespace-nowrap">Turn Your Ideas Into</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#4531FF] to-[#00C6FF] drop-shadow-sm">
                  Movies, Music Videos & Ads
                </span>
              </h1>

              {/* Value Proposition - More Specific */}
              <p className="text-lg lg:text-xl text-[#A3A6B2] mb-8 leading-relaxed max-w-2xl mx-auto">
                Just type a prompt. AI creates your cinematic film, music video, or commercial in minutes.
              </p>

              {/* Primary Actions - Clear Hierarchy */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <button
                  onClick={() => setShowComingSoon(true)}
                  className="bg-gradient-to-r from-[#4531FF] to-[#00C6FF] text-white text-lg font-semibold px-8 py-4 rounded-full hover:shadow-lg transition-all duration-200 hover:scale-105 hover:from-[#3420E5] hover:to-[#00A3D9] inline-flex items-center gap-2 hover:-translate-y-0.5 hover:brightness-110 group"
                >
                  Start Creating Free
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <Link
                  href="#interactive-demo"
                  className="text-lg px-8 py-4 group border-2 border-[#4531FF] hover:border-[#00C6FF] hover:bg-[#00C6FF]/10 hover:shadow-[inset_0_0_20px_rgba(0,198,255,0.2)] transition-all rounded-full bg-transparent text-white font-medium inline-flex items-center gap-2 hover:-translate-y-0.5"
                >
                  <PlayIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  See How It Works
                </Link>
              </div>

              {/* Dynamic Social Proof */}
              <div className="bg-[#1F2233] backdrop-blur-md rounded-2xl p-6 border border-[#4531FF]/20 max-w-sm mx-auto mt-8">
                <div key={currentStat} className="flex items-center gap-4 animate-fade-in justify-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FFB457]/20 to-[#FFB457]/10 rounded-xl flex items-center justify-center">
                    {React.createElement(stats[currentStat].icon, {
                      className: `w-6 h-6 text-[#FFB457]`,
                    })}
                  </div>
                  <div className="text-left">
                    <div className="text-3xl font-bold text-white">{stats[currentStat].value}</div>
                    <div className="text-sm text-[#A3A6B2] font-semibold">{stats[currentStat].label}</div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Feature Benefits Strip */}
          <div className="mt-20 pt-12 border-t border-white/10">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20 rounded-xl flex items-center justify-center mb-4">
                  <SparklesIcon className="w-6 h-6 text-[#00C6FF]" />
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-2 leading-normal">AI Director</h3>
                <p className="text-sm text-[#9EA1AE] leading-normal">Turns your story into cinematic scenes</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-[#00C6FF]" />
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-2 leading-normal">Minutes Not Months</h3>
                <p className="text-sm text-[#9EA1AE] leading-normal">Create your film in under 3 minutes</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-[#4531FF]/20 to-[#00C6FF]/20 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-[#00C6FF]" />
                </div>
                <h3 className="text-lg lg:text-xl font-semibold text-white mb-2 leading-normal">No Experience Needed</h3>
                <p className="text-sm text-[#9EA1AE] leading-normal">From first-time creators to filmmakers</p>
              </div>
            </div>
          </div>

          {/* Bottom Section - Social Proof */}
          <div className="text-center mt-16">
            <p className="text-[#9EA1AE] mb-4">Join thousands making their first films</p>
            <div className="flex justify-center items-center gap-8 opacity-60">
              <div className="text-xl font-medium text-white/50">Music Videos</div>
              <div className="text-xl font-medium text-white/50">Commercials</div>
              <div className="text-xl font-medium text-white/50">Short Films</div>
              <div className="text-xl font-medium text-white/50">Brand Content</div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal - Enhanced */}
      {isVideoPlaying && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={() => setIsVideoPlaying(false)}
        >
          <div className="max-w-5xl w-full bg-white/95 backdrop-blur-xl rounded-3xl p-3 shadow-2xl">
            <div className="aspect-video bg-black rounded-2xl flex items-center justify-center">
              <p className="text-white text-lg">Demo Video Player</p>
            </div>
          </div>
        </div>
      )}

      {/* Coming Soon Modal */}
      <ComingSoonModal isOpen={showComingSoon} onClose={() => setShowComingSoon(false)} />
    </section>
  );
}

// Add custom CSS for animations
const style = `
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fade-in {
    animation: fade-in 0.5s ease-out;
  }
  
  @keyframes pulse-slow {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  .animate-pulse-slow {
    animation: pulse-slow 6s ease-in-out infinite;
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = style;
  document.head.appendChild(styleSheet);
}